import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Plus,
  Trash2,
  Upload,
  Camera,
  Cloud,
  CloudOff,
  Loader2,
  X,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Label } from '@/app/components/ui/label';
import { Checkbox } from '@/app/components/ui/checkbox';
import { Badge } from '@/app/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Switch } from '@/app/components/ui/switch';
import { useProjectAssignments } from '@/hooks/use-projects';
import { usePersons } from '@/hooks/use-persons';
import { usePlatformModules } from '@/hooks/use-modules';
import {
  useCreateDailyReport,
  useUpdateDailyReport,
  useUploadPhoto,
  useDailyReport,
} from '@/hooks/useDailyReports';
import {
  useOfflineDraft,
  enqueueSubmission,
  type DraftDelay,
  type DraftData,
} from './use-offline-draft';
import type { DelayType, ProjectAssignment } from '@/lib/types';

const DELAY_TYPES: { value: DelayType; label: string }[] = [
  { value: 'weather', label: 'Weather' },
  { value: 'client', label: 'Client' },
  { value: 'safety', label: 'Safety' },
  { value: 'access', label: 'Access' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'permit', label: 'Permit' },
  { value: 'rescue', label: 'Rescue' },
  { value: 'standby', label: 'Standby' },
  { value: 'other', label: 'Other' },
];

const STEPS = [
  'Date & Weather',
  'Crew on Site',
  'Work & Photos',
  'Delays',
  'Review & Submit',
];

interface CrewOption {
  id: number;
  name: string;
  role: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

export function dataUrlToFile(dataUrl: string, filename: string): File {
  const [meta, base64] = dataUrl.split(',');
  const mime = meta.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
  const bin = atob(base64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], filename, { type: mime });
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─── Step Indicator ─────────────────────────────────────────────────────────

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1 w-full">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 flex-1 rounded-full transition-colors ${
            i <= current ? 'bg-primary' : 'bg-gray-200'
          }`}
        />
      ))}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function CreateDailyReportPage() {
  const { id: projectIdParam, reportId } = useParams<{
    id: string;
    reportId: string;
  }>();
  const projectId = Number(projectIdParam);
  const existingId = reportId ? Number(reportId) : undefined;
  const navigate = useNavigate();

  // Existing report data (edit mode)
  const { data: existingReport } = useDailyReport(existingId ?? 0);

  const { isModuleEnabled } = usePlatformModules();
  const crewEnabled = isModuleEnabled('crew');
  const { data: personsData } = usePersons({ page_size: 1000 }, !crewEnabled);
  const persons = personsData?.results ?? [];

  const { draft, updateField, clearDraft, hasDraft } = useOfflineDraft(
    projectId,
    existingId
  );
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isOnline = useIsOnline();

  // Seed draft from existing report on first load (edit mode)
  useEffect(() => {
    if (existingReport && !hasDraft) {
      updateField('date', existingReport.date);
      updateField('weather', existingReport.weather);
      updateField('shift_start', existingReport.shift_start ?? '07:00');
      updateField('shift_end', existingReport.shift_end ?? '17:00');
      updateField('productive_hours', existingReport.productive_hours ?? '');
      updateField('notes', existingReport.notes);
      updateField('supervisor', existingReport.supervisor);
      updateField(
        'crewIds',
        existingReport.crew_entries.map((c) => c.person ?? c.tech)
      );
      const roles: Record<number, string> = {};
      const hours: Record<number, string> = {};
      existingReport.crew_entries.forEach((c) => {
        const id = c.person ?? c.tech;
        roles[id] = c.role;
        hours[id] = c.hours_on_site ?? '';
      });
      updateField('crewRoles', roles);
      updateField('crewHours', hours);
      updateField(
        'delays',
        existingReport.delay_entries.map((d) => ({
          delay_type: d.delay_type,
          start_time: d.start_time ?? '',
          end_time: d.end_time ?? '',
          billable: d.billable,
          client_approval_required: d.client_approval_required,
          description: d.description,
          rate_override: d.rate_override ?? '',
          photoFiles: [],
          photoCaptions: [],
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingReport]);

  // Crew assignments for this project
  const { data: assignments } = useProjectAssignments(projectId);

  const crewOptions: CrewOption[] = useMemo(() => {
    if (crewEnabled && assignments && assignments.length > 0) {
      return assignments.map((a) => ({
        id: a.person ?? a.tech,
        name: a.tech_name,
        role: a.role,
      }));
    }
    return persons.map((p) => ({ id: p.id, name: p.full_name, role: '' }));
  }, [crewEnabled, assignments, persons]);

  // Mutations
  const createReport = useCreateDailyReport();
  const updateReport = useUpdateDailyReport();
  const uploadPhoto = useUploadPhoto();

  const goBack = () => navigate(`/work/daily-reports/${projectId}`);
  const prev = () => setStep((s) => Math.max(0, s - 1));
  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));

  // ─── Submit ─────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    if (draft.crewIds.length === 0) {
      setSubmitError('Select at least one crew member.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    const payload = {
      project: projectId,
      date: draft.date,
      weather: draft.weather,
      shift_start: draft.shift_start || null,
      shift_end: draft.shift_end || null,
      productive_hours: draft.productive_hours || null,
      notes: draft.notes,
      supervisor: draft.supervisor,
    };

    if (!isOnline) {
      // Queue for later
      enqueueSubmission({
        id: crypto.randomUUID(),
        projectId,
        payload,
        crewPayloads: draft.crewIds.map((personId) => ({
          person: personId,
          role: draft.crewRoles[personId] ?? '',
          hours_on_site: draft.crewHours[personId] ?? '',
        })),
        delayPayloads: draft.delays,
        photoDataUrls: draft.photoFiles,
        photoCaptions: draft.photoCaptions,
        queuedAt: new Date().toISOString(),
      });
      clearDraft();
      navigate(`/work/daily-reports/${projectId}`);
      setSubmitting(false);
      return;
    }

    try {
      // Build inline nested arrays so the backend saves the report as a
      // draft first, then we attach photos, then mark submitted.
      // This avoids the report locking before photo uploads run.
      const crewEntries = draft.crewIds.map((personId) => ({
        person: personId,
        role: draft.crewRoles[personId] ?? '',
        hours_on_site: draft.crewHours[personId] || null,
      }));

      const delayEntries = draft.delays.map((delay) => ({
        delay_type: delay.delay_type as DelayType,
        start_time: delay.start_time || null,
        end_time: delay.end_time || null,
        billable: delay.billable,
        client_approval_required: delay.client_approval_required,
        description: delay.description,
        rate_override: delay.billable ? (delay.rate_override || null) : null,
      }));

      let report;
      if (existingId) {
        report = await updateReport.mutateAsync({
          id: existingId,
          data: {
            ...payload,
            crew_entries: crewEntries,
            delay_entries: delayEntries,
          },
        });
      } else {
        report = await createReport.mutateAsync({
          ...payload,
          crew_entries: crewEntries,
          delay_entries: delayEntries,
        });
      }

      // Upload general photos (still separate — multipart file uploads)
      for (let i = 0; i < draft.photoFiles.length; i++) {
        const file = dataUrlToFile(
          draft.photoFiles[i],
          `photo_${i}.jpg`
        );
        await uploadPhoto.mutateAsync({
          dailyReportId: report.id,
          file,
          caption: draft.photoCaptions[i] ?? '',
        });
      }

      // Upload delay-attached photos — match by fields, not index,
      // because the server returns delay_entries sorted by start_time.
      const createdDelays = report.delay_entries ?? [];
      const matchedIds = new Set<number>();
      for (let di = 0; di < draft.delays.length; di++) {
        const draftDelay = draft.delays[di];
        if (!draftDelay.photoFiles?.length) continue;
        const serverDelay = createdDelays.find(
          (sd) =>
            !matchedIds.has(sd.id) &&
            sd.delay_type === draftDelay.delay_type &&
            (sd.start_time ?? '').slice(0, 5) === (draftDelay.start_time || '').slice(0, 5) &&
            (sd.end_time ?? '').slice(0, 5) === (draftDelay.end_time || '').slice(0, 5) &&
            (sd.description ?? '') === (draftDelay.description || '')
        );
        if (!serverDelay) {
          console.warn(
            `[delay-photos] Could not match draft delay #${di} (${draftDelay.delay_type}) to a server delay — ${draftDelay.photoFiles.length} photo(s) skipped`,
          );
          continue;
        }
        matchedIds.add(serverDelay.id);
        for (let pi = 0; pi < draftDelay.photoFiles.length; pi++) {
          const file = dataUrlToFile(
            draftDelay.photoFiles[pi],
            `delay_${di}_photo_${pi}.jpg`
          );
          await uploadPhoto.mutateAsync({
            dailyReportId: report.id,
            file,
            caption: draftDelay.photoCaptions[pi] ?? '',
            delayEntryId: serverDelay.id,
          });
        }
      }

      // Mark as submitted only after all child resources (crew, delays,
      // photos) have been created successfully.
      await updateReport.mutateAsync({
        id: report.id,
        data: { submitted_at: new Date().toISOString() },
      });

      clearDraft();
      navigate(`/work/daily-reports/${projectId}`);
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to submit report'
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    projectId,
    existingId,
    draft,
    isOnline,
    createReport,
    updateReport,
    uploadPhoto,
    clearDraft,
    navigate,
  ]);

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b px-4 py-3 space-y-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={goBack} className="shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold truncate">
              {existingId ? 'Edit Daily Report' : 'New Daily Report'}
            </h1>
            <p className="text-xs text-muted-foreground">
              Step {step + 1} of {STEPS.length}: {STEPS[step]}
            </p>
          </div>
          {!isOnline && (
            <Badge variant="outline" className="shrink-0 gap-1 text-amber-600 border-amber-300">
              <CloudOff className="h-3 w-3" /> Offline
            </Badge>
          )}
          {isOnline && (
            <Badge variant="outline" className="shrink-0 gap-1 text-green-600 border-green-300">
              <Cloud className="h-3 w-3" /> Online
            </Badge>
          )}
        </div>
        <StepIndicator current={step} total={STEPS.length} />
        {hasDraft && (
          <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5 text-xs text-amber-700 flex items-center gap-2">
            <CloudOff className="h-3 w-3 shrink-0" />
            Draft saved locally — your changes are preserved.
          </div>
        )}
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {step === 0 && (
          <StepDateWeather
            draft={draft}
            updateField={updateField}
            assignments={assignments}
          />
        )}
        {step === 1 && (
          <StepCrew
            draft={draft}
            updateField={updateField}
            options={crewOptions}
            crewEnabled={crewEnabled}
          />
        )}
        {step === 2 && (
          <StepWorkPhotos draft={draft} updateField={updateField} />
        )}
        {step === 3 && <StepDelays draft={draft} updateField={updateField} />}
        {step === 4 && (
          <StepReview
            draft={draft}
            options={crewOptions}
            submitting={submitting}
            submitError={submitError}
          />
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="sticky bottom-0 bg-white border-t px-4 py-3 flex gap-3">
        {step > 0 && (
          <Button
            type="button"
            variant="outline"
            className="flex-1 h-12 text-base"
            onClick={prev}
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
        )}
        {step < STEPS.length - 1 && (
          <Button
            type="button"
            className="flex-1 h-12 text-base"
            onClick={next}
          >
            Next <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
        {step === STEPS.length - 1 && (
          <Button
            type="button"
            className="flex-1 h-12 text-base"
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…
              </>
            ) : !isOnline ? (
              <>
                <CloudOff className="h-4 w-4 mr-2" /> Queue Offline
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" /> Submit Report
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Online State Hook ──────────────────────────────────────────────────────

function useIsOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

// ─── Step 1: Date & Weather ─────────────────────────────────────────────────

function StepDateWeather({
  draft,
  updateField,
  assignments,
}: {
  draft: DraftData;
  updateField: <K extends keyof DraftData>(field: K, value: DraftData[K]) => void;
  assignments?: ProjectAssignment[];
}) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Report Date</CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            type="date"
            value={draft.date}
            onChange={(e) => updateField('date', e.target.value)}
            className="h-12 text-base"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Weather Conditions</CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            placeholder="e.g. Sunny, 75°F, light wind"
            value={draft.weather}
            onChange={(e) => updateField('weather', e.target.value)}
            className="h-12 text-base"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Shift Times</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Start</Label>
              <Input
                type="time"
                value={draft.shift_start}
                onChange={(e) => updateField('shift_start', e.target.value)}
                className="h-12 text-base"
              />
            </div>
            <div>
              <Label className="text-sm">End</Label>
              <Input
                type="time"
                value={draft.shift_end}
                onChange={(e) => updateField('shift_end', e.target.value)}
                className="h-12 text-base"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {assignments && assignments.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Supervisor</CardTitle>
          </CardHeader>
          <CardContent>
            <Select
              value={draft.supervisor ? String(draft.supervisor) : ''}
              onValueChange={(v) =>
                updateField('supervisor', v ? Number(v) : null)
              }
            >
              <SelectTrigger className="h-12 text-base">
                <SelectValue placeholder="Select supervisor (optional)" />
              </SelectTrigger>
              <SelectContent>
                {assignments.map((a) => (
                  <SelectItem key={a.tech} value={String(a.tech)}>
                    {a.tech_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Step 2: Crew on Site ───────────────────────────────────────────────────

function StepCrew({
  draft,
  updateField,
  options,
  crewEnabled,
}: {
  draft: DraftData;
  updateField: <K extends keyof DraftData>(field: K, value: DraftData[K]) => void;
  options: CrewOption[];
  crewEnabled: boolean;
}) {
  const toggleCrew = (id: number) => {
    const ids = draft.crewIds.includes(id)
      ? draft.crewIds.filter((crewId) => crewId !== id)
      : [...draft.crewIds, id];
    updateField('crewIds', ids);
  };

  const setRole = (id: number, role: string) => {
    updateField('crewRoles', { ...draft.crewRoles, [id]: role });
  };

  const setHours = (id: number, hours: string) => {
    updateField('crewHours', { ...draft.crewHours, [id]: hours });
  };

  if (options.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          {crewEnabled
            ? 'No crew assigned to this project yet. Add assignments in the project detail page first.'
            : 'No people found in your organization. Add people first.'}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Tap to select crew members on site today. {draft.crewIds.length} selected.
      </p>
      {options.map((option) => {
        const selected = draft.crewIds.includes(option.id);
        return (
          <Card
            key={option.id}
            className={`transition-colors cursor-pointer ${
              selected ? 'border-primary bg-primary/5' : ''
            }`}
          >
            <CardContent className="py-3 px-4">
              <div
                className="flex items-center gap-3"
                onClick={() => toggleCrew(option.id)}
              >
                <Checkbox checked={selected} className="h-5 w-5" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{option.name}</p>
                  {option.role && (
                    <p className="text-xs text-muted-foreground">{option.role}</p>
                  )}
                </div>
              </div>
              {selected && (
                <div className="mt-3 grid grid-cols-2 gap-2 pl-8">
                  <div>
                    <Label className="text-xs">Role today</Label>
                    <Input
                      placeholder="Role"
                      value={draft.crewRoles[option.id] ?? option.role}
                      onChange={(e) => setRole(option.id, e.target.value)}
                      className="h-10 text-sm"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Hours</Label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      max="24"
                      placeholder="8"
                      value={draft.crewHours[option.id] ?? ''}
                      onChange={(e) => setHours(option.id, e.target.value)}
                      className="h-10 text-sm"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ─── Step 3: Work & Photos ──────────────────────────────────────────────────

function StepWorkPhotos({
  draft,
  updateField,
}: {
  draft: DraftData;
  updateField: <K extends keyof DraftData>(field: K, value: DraftData[K]) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    const newPhotos: string[] = [];
    const newCaptions: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const dataUrl = await fileToDataUrl(files[i]);
      newPhotos.push(dataUrl);
      newCaptions.push('');
    }
    updateField('photoFiles', [...draft.photoFiles, ...newPhotos]);
    updateField('photoCaptions', [...draft.photoCaptions, ...newCaptions]);
  };

  const removePhoto = (index: number) => {
    updateField(
      'photoFiles',
      draft.photoFiles.filter((_, i) => i !== index)
    );
    updateField(
      'photoCaptions',
      draft.photoCaptions.filter((_, i) => i !== index)
    );
  };

  const updateCaption = (index: number, caption: string) => {
    const caps = [...draft.photoCaptions];
    caps[index] = caption;
    updateField('photoCaptions', caps);
  };

  // Drag and drop
  const [dragOver, setDragOver] = useState(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Productive Hours</CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            type="number"
            step="0.5"
            min="0"
            max="24"
            placeholder="e.g. 8"
            value={draft.productive_hours}
            onChange={(e) => updateField('productive_hours', e.target.value)}
            className="h-12 text-base"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Work completed, progress notes, issues..."
            value={draft.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            rows={4}
            className="text-base"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Photos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragOver
                ? 'border-primary bg-primary/5'
                : 'border-gray-200'
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center gap-2">
              <Upload className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Drag & drop photos here
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-1" /> Browse
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => cameraInputRef.current?.click()}
                >
                  <Camera className="h-4 w-4 mr-1" /> Camera
                </Button>
              </div>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />

          {/* Photo previews */}
          {draft.photoFiles.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              {draft.photoFiles.map((src, i) => (
                <div key={i} className="relative group">
                  <img
                    src={src}
                    alt={`Photo ${i + 1}`}
                    className="w-full aspect-square object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <Input
                    placeholder="Caption"
                    value={draft.photoCaptions[i] ?? ''}
                    onChange={(e) => updateCaption(i, e.target.value)}
                    className="mt-1 h-8 text-xs"
                  />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Step 4: Delays ─────────────────────────────────────────────────────────

function StepDelays({
  draft,
  updateField,
}: {
  draft: DraftData;
  updateField: <K extends keyof DraftData>(field: K, value: DraftData[K]) => void;
}) {
  const { isModuleEnabled } = usePlatformModules();
  const billingEnabled = isModuleEnabled('projects');

  const addDelay = () => {
    updateField('delays', [
      ...draft.delays,
      {
        delay_type: 'weather',
        start_time: '',
        end_time: '',
        billable: false,
        client_approval_required: false,
        description: '',
        rate_override: '',
        photoFiles: [],
        photoCaptions: [],
      },
    ]);
  };

  const removeDelay = (index: number) => {
    updateField(
      'delays',
      draft.delays.filter((_, i) => i !== index)
    );
  };

  const updateDelay = (
    index: number,
    field: keyof DraftDelay,
    value: string | boolean
  ) => {
    const updated = [...draft.delays];
    updated[index] = { ...updated[index], [field]: value };
    // Auto-set defaults when switching to standby (billing enrichment only)
    if (billingEnabled && field === 'delay_type' && value === 'standby') {
      updated[index].billable = true;
      updated[index].client_approval_required = true;
    }
    updateField('delays', updated);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {draft.delays.length === 0
            ? 'No delays recorded'
            : `${draft.delays.length} delay(s)`}
        </p>
        <Button type="button" size="sm" onClick={addDelay}>
          <Plus className="h-4 w-4 mr-1" /> Add Delay
        </Button>
      </div>

      {draft.delays.map((delay, i) => (
        <Card key={i}>
          <CardContent className="py-3 px-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Delay #{i + 1}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-red-500"
                onClick={() => removeDelay(i)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <div>
              <Label className="text-xs">Type</Label>
              <Select
                value={delay.delay_type}
                onValueChange={(v) => updateDelay(i, 'delay_type', v)}
              >
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DELAY_TYPES.map((dt) => (
                    <SelectItem key={dt.value} value={dt.value}>
                      {dt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Start</Label>
                <Input
                  type="time"
                  value={delay.start_time}
                  onChange={(e) => updateDelay(i, 'start_time', e.target.value)}
                  className="h-10"
                />
              </div>
              <div>
                <Label className="text-xs">End</Label>
                <Input
                  type="time"
                  value={delay.end_time}
                  onChange={(e) => updateDelay(i, 'end_time', e.target.value)}
                  className="h-10"
                />
              </div>
            </div>

            {billingEnabled && (
              <>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={delay.billable}
                    onCheckedChange={(v) => updateDelay(i, 'billable', v)}
                  />
                  <Label className="text-sm">Billable</Label>
                </div>

                {delay.billable && (
                  <div>
                    <Label className="text-xs">Rate override ($/hr)</Label>
                    <Input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      placeholder="Project default"
                      value={delay.rate_override}
                      onChange={(e) => updateDelay(i, 'rate_override', e.target.value)}
                      className="h-10"
                    />
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Switch
                    checked={delay.client_approval_required}
                    onCheckedChange={(v) => updateDelay(i, 'client_approval_required', v)}
                  />
                  <Label className="text-sm">Requires client approval</Label>
                </div>
              </>
            )}

            {billingEnabled && delay.delay_type === 'standby' && (
              <p className="text-xs text-muted-foreground">
                Standby delays default to billable with client approval required.
                The project's standby rate will be used if set.
              </p>
            )}
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea
                placeholder="Describe the delay..."
                value={delay.description}
                onChange={(e) => updateDelay(i, 'description', e.target.value)}
                rows={2}
                className="text-sm"
              />
            </div>

            <DelayPhotoAttach
              delay={delay}
              delayIndex={i}
              delays={draft.delays}
              updateField={updateField}
            />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Delay Photo Attach ─────────────────────────────────────────────────────

function DelayPhotoAttach({
  delay,
  delayIndex,
  delays,
  updateField,
}: {
  delay: DraftDelay;
  delayIndex: number;
  delays: DraftDelay[];
  updateField: <K extends keyof DraftData>(field: K, value: DraftData[K]) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const photos = delay.photoFiles ?? [];
  const captions = delay.photoCaptions ?? [];

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    const newPhotos: string[] = [];
    const newCaptions: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const dataUrl = await fileToDataUrl(files[i]);
      newPhotos.push(dataUrl);
      newCaptions.push('');
    }
    const updated = [...delays];
    updated[delayIndex] = {
      ...updated[delayIndex],
      photoFiles: [...photos, ...newPhotos],
      photoCaptions: [...captions, ...newCaptions],
    };
    updateField('delays', updated);
  };

  const removePhoto = (photoIndex: number) => {
    const updated = [...delays];
    updated[delayIndex] = {
      ...updated[delayIndex],
      photoFiles: photos.filter((_, i) => i !== photoIndex),
      photoCaptions: captions.filter((_, i) => i !== photoIndex),
    };
    updateField('delays', updated);
  };

  const updateCaption = (photoIndex: number, caption: string) => {
    const updated = [...delays];
    const newCaptions = [...captions];
    newCaptions[photoIndex] = caption;
    updated[delayIndex] = {
      ...updated[delayIndex],
      photoCaptions: newCaptions,
    };
    updateField('delays', updated);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          onClick={() => fileRef.current?.click()}
        >
          <Camera className="h-3.5 w-3.5 mr-1" /> Attach Photo
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          onClick={() => cameraRef.current?.click()}
        >
          <Camera className="h-3.5 w-3.5 mr-1" /> Take Photo
        </Button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((src, pi) => (
            <div key={pi} className="relative group">
              <img
                src={src}
                alt={`Delay photo ${pi + 1}`}
                className="w-full aspect-square object-cover rounded-md"
              />
              <button
                type="button"
                onClick={() => removePhoto(pi)}
                className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-2.5 w-2.5" />
              </button>
              <Input
                placeholder="Caption"
                value={captions[pi] ?? ''}
                onChange={(e) => updateCaption(pi, e.target.value)}
                className="mt-1 h-7 text-xs"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Step 5: Review & Submit ────────────────────────────────────────────────

function StepReview({
  draft,
  options,
  submitting,
  submitError,
}: {
  draft: DraftData;
  options: CrewOption[];
  submitting: boolean;
  submitError: string | null;
}) {
  const crewNames = draft.crewIds
    .map((id) => options.find((o) => o.id === id)?.name ?? `Crew #${id}`)
    .join(', ');

  const delayLabel = (dt: string) =>
    DELAY_TYPES.find((d) => d.value === dt)?.label ?? dt;

  return (
    <div className="space-y-4">
      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700">
          {submitError}
        </div>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Date & Weather</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <p>
            <span className="font-medium">Date:</span> {draft.date}
          </p>
          <p>
            <span className="font-medium">Weather:</span>{' '}
            {draft.weather || '—'}
          </p>
          <p>
            <span className="font-medium">Shift:</span> {draft.shift_start} –{' '}
            {draft.shift_end}
          </p>
        </CardContent>
      </Card>

      <Card
        data-required="true"
        data-required-empty={draft.crewIds.length === 0 ? 'true' : 'false'}
        data-required-message="Select at least one crew member."
        tabIndex={-1}
        className={draft.crewIds.length === 0 && submitError ? 'border-red-500' : undefined}
      >
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Crew ({draft.crewIds.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          {crewNames || <span className="text-muted-foreground">None selected</span>}
          {draft.crewIds.length === 0 && submitError && (
            <p className="mt-2 text-sm text-red-600">{submitError}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Work Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <p>
            <span className="font-medium">Productive Hours:</span>{' '}
            {draft.productive_hours || '—'}
          </p>
          <p>
            <span className="font-medium">Notes:</span>{' '}
            {draft.notes || '—'}
          </p>
          <p>
            <span className="font-medium">Photos:</span>{' '}
            {draft.photoFiles.length}
          </p>
        </CardContent>
      </Card>

      {draft.delays.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Delays ({draft.delays.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {draft.delays.map((d, i) => (
              <div
                key={i}
                className="border rounded-md px-3 py-2 text-sm space-y-0.5"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="secondary" className="text-xs">
                    {delayLabel(d.delay_type)}
                  </Badge>
                  {d.billable && (
                    <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                      Billable
                    </Badge>
                  )}
                  {d.client_approval_required && (
                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                      Client approval
                    </Badge>
                  )}
                  {d.billable && d.rate_override && (
                    <Badge variant="outline" className="text-xs">
                      ${d.rate_override}/hr
                    </Badge>
                  )}
                </div>
                {(d.start_time || d.end_time) && (
                  <p className="text-xs text-muted-foreground">
                    {d.start_time} – {d.end_time}
                  </p>
                )}
                {d.description && <p className="text-xs">{d.description}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {submitting && (
        <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Submitting report…
        </div>
      )}
    </div>
  );
}
